import { isNotificationPortfolioId } from "./productUpdateNotifications";
import type { ProductNotificationTarget } from "../types/productNotificationMappings";

const STATE_KEY = "shareSignInTradeAlerts";
type ProductType = ProductNotificationTarget["productType"];
interface Handoff {
  token: string;
  userId: string;
  pathname: string;
  productType: ProductType;
  productId: number;
}

// Only the latest successful sign-in in this tab may hand off an auto-enable.
// Keeping the ticket in memory also makes restored history inert after reload.
let pending: Handoff | null = null;

function stateRecord(state: unknown): Record<string, unknown> | null {
  return state !== null && typeof state === "object" && !Array.isArray(state)
    ? state as Record<string, unknown>
    : null;
}

// Portfolio and basket detail destinations qualify. The control that consumes
// the ticket must present the same product type and ID as the destination.
export function createShareSignInTradeAlertsState(userId: string | undefined, destination: string | null) {
  pending = null;
  const pathname = destination?.split(/[?#]/, 1)[0];
  const match = pathname?.match(/^\/alpha\/discover\/(portfolio|basket)\/([1-9]\d*)\/([1-9]\d*)\/?$/);
  if (!userId || !pathname || !match || !isNotificationPortfolioId(Number(match[2]))
    || !isNotificationPortfolioId(Number(match[3]))) return undefined;
  const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  pending = {
    token, userId, pathname,
    productType: match[1] === "basket" ? "BASKET" : "PORTFOLIO",
    productId: Number(match[3]),
  };
  return { [STATE_KEY]: token };
}

export function hasShareSignInTradeAlertsState(state: unknown): boolean {
  return Object.prototype.hasOwnProperty.call(stateRecord(state) ?? {}, STATE_KEY);
}

export function withoutShareSignInTradeAlertsState(state: unknown) {
  const { [STATE_KEY]: _marker, ...rest } = stateRecord(state) ?? {};
  return rest;
}

export function consumeShareSignInTradeAlerts(
  state: unknown,
  userId: string | undefined,
  pathname: string,
  productType: ProductType,
  productId: number,
  navigationType: string,
): boolean {
  if (!pending || stateRecord(state)?.[STATE_KEY] !== pending.token) return false;
  const handoff = pending;
  pending = null;
  return navigationType === "REPLACE" && handoff.userId === userId && handoff.pathname === pathname
    && handoff.productType === productType && handoff.productId === productId;
}
